import i18next, { type BackendModule, type i18n as I18nInstance } from 'i18next';
import ICU from 'i18next-icu';
import { initReactI18next } from 'react-i18next';
import {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  directionOf,
  intlLocaleWithNumerals,
  isLocale,
  type Locale,
  type Numerals,
} from './locales';

export const NAMESPACES = ['common', 'auth', 'nav', 'dashboard', 'sessions', 'admin', 'progress', 'quran', 'messages', 'platform', 'files', 'notifications', 'assignments', 'exams', 'calendar', 'support', 'legal', 'help', 'push', 'consents', 'moderation', 'landing'] as const;
export type Namespace = (typeof NAMESPACES)[number];

type Catalog = Record<string, string>;

// Catalogs are split per locale/namespace and loaded on demand (one chunk each).
const loaders = import.meta.glob<{ default: Catalog }>('../../../locales/*/*.json');

const lazyCatalogBackend: BackendModule = {
  type: 'backend',
  init() {
    /* no options */
  },
  read(language, namespace, callback) {
    const load = loaders[`../../../locales/${language}/${namespace}.json`];
    if (!load) {
      callback(new Error(`Missing catalog ${language}/${namespace}`), false);
      return;
    }
    load().then(
      (module) => callback(null, module.default),
      (error: unknown) => callback(error instanceof Error ? error : new Error(String(error)), false),
    );
  },
};

let activeNumerals: Numerals = 'latn';
const icu = new ICU();

/**
 * ICU MessageFormat gets a locale tag carrying the numbering system so `#` in plurals
 * and `{n, number}` follow the user's numerals preference (Western digits by default).
 */
function icuLocale(lng: string): string {
  return intlLocaleWithNumerals(isLocale(lng) ? lng : DEFAULT_LOCALE, activeNumerals);
}

export const i18n: I18nInstance = i18next.createInstance();

export async function initI18n(lng: Locale): Promise<I18nInstance> {
  if (i18n.isInitialized) {
    await i18n.changeLanguage(lng);
    return i18n;
  }
  await i18n
    .use(icu)
    .use(lazyCatalogBackend)
    .use(initReactI18next)
    .init({
      lng,
      fallbackLng: DEFAULT_LOCALE,
      supportedLngs: [...SUPPORTED_LOCALES],
      load: 'languageOnly',
      ns: [...NAMESPACES],
      defaultNS: 'common',
      keySeparator: false, // flat keys inside each namespace (i18n doc §7.1)
      nsSeparator: ':',
      interpolation: { escapeValue: false },
      returnEmptyString: false,
      i18nFormat: { parseLngForICU: icuLocale },
      // Dev guard: any key missing from a catalog is reported loudly in the console.
      saveMissing: import.meta.env.DEV,
      missingKeyHandler: (lngs, ns, key) => {
        // With useSuspense off, components can render before a namespace has loaded; those lookups
        // are not missing keys. Report only keys absent from catalogs that are actually loaded.
        if (!import.meta.env.DEV || !lngs.every((l) => i18n.hasResourceBundle(l, ns))) return;
        console.error(`[i18n] missing key ${ns}:${key} (${lngs.join(',')})`);
      },
      react: { useSuspense: false },
    });
  return i18n;
}

/** Sets lang/dir on <html> — the only place direction is decided (a11y doc §11.1). */
export function applyDocumentLocale(lng: Locale): void {
  const root = document.documentElement;
  root.lang = lng;
  root.dir = directionOf(lng);
}

export async function changeLocale(lng: Locale): Promise<void> {
  await i18n.changeLanguage(lng);
  applyDocumentLocale(lng);
}

/** Switch digits for ICU messages; re-renders translated components. */
export async function setMessageNumerals(numerals: Numerals): Promise<void> {
  if (numerals === activeNumerals) return;
  activeNumerals = numerals;
  icu.clearCache();
  if (i18n.isInitialized) await i18n.changeLanguage(i18n.language);
}

export function currentLocale(): Locale {
  const lng = i18n.resolvedLanguage ?? i18n.language;
  return isLocale(lng) ? lng : DEFAULT_LOCALE;
}
